import { useState, useEffect, useRef } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useBranding } from "../context/BrandingContext";
import api, { BACKEND_URL } from "../lib/api";
import {
  LayoutDashboard, ShoppingCart, Package, CalendarClock, CalendarDays,
  Users as UsersIcon, Truck, TrendingUp, TrendingDown, Wallet, BarChart3,
  UserCog, ShieldCheck, Settings as SettingsIcon, Bell, LogOut, Menu, X,
  ChevronDown, ClipboardList, History,
} from "lucide-react";

const NAV = [
  { label: "Dashboard", path: "/", icon: LayoutDashboard, module: "dashboard" },
  {
    label: "Satış", icon: ShoppingCart, children: [
      { label: "Satış Ekranı", path: "/satis", module: "sales" },
      { label: "Satış Geçmişi", path: "/satis-gecmisi", module: "sales_history" },
    ],
  },
  {
    label: "Stok Yönetimi", icon: Package, children: [
      { label: "Ana Kategoriler", path: "/ana-kategoriler", module: "categories" },
      { label: "Alt Kategoriler", path: "/alt-kategoriler", module: "subcategories" },
      { label: "Ürünler", path: "/urunler", module: "products" },
      { label: "Stok Hareketleri", path: "/stok-hareketleri", module: "stock_movements" },
      { label: "Envanter", path: "/envanter", module: "equipment" },
    ],
  },
  { label: "Ajanda", path: "/ajanda", icon: ClipboardList, module: "agenda" },
  { label: "Takvim", path: "/takvim", icon: CalendarDays, module: "calendar" },
  { label: "Randevular", path: "/randevular", icon: CalendarClock, module: "appointments" },
  { label: "Müşteriler", path: "/musteriler", icon: UsersIcon, module: "customers" },
  { label: "Tedarikçiler", path: "/tedarikciler", icon: Truck, module: "suppliers" },
  { label: "Gelir", path: "/gelir", icon: TrendingUp, module: "income" },
  { label: "Gider", path: "/gider", icon: TrendingDown, module: "expense" },
  { label: "Kasa", path: "/kasa", icon: Wallet, module: "cash" },
  { label: "Raporlar", path: "/raporlar", icon: BarChart3, module: "reports" },
  { label: "Kullanıcılar", path: "/kullanicilar", icon: UserCog, module: "users" },
  { label: "Yetkilendirme", path: "/yetkilendirme", icon: ShieldCheck, module: "permissions" },
  { label: "Ayarlar", path: "/ayarlar", icon: SettingsIcon, module: "settings" },
  { label: "Denetim Kaydı", path: "/denetim-kayitlari", icon: History, module: "audit_log" },
];

const NavItem = ({ item, onNavigate }) => {
  const location = useLocation();
  const isChildActive = item.children?.some((c) => c.path === location.pathname);
  const [open, setOpen] = useState(isChildActive);

  if (item.children) {
    return (
      <div>
        <button
          data-testid={`nav-group-${item.label}`}
          onClick={() => setOpen(!open)}
          className="flex w-full items-center justify-between px-5 py-2.5 text-sm text-white/70 transition-colors duration-200 hover:bg-white/5 hover:text-white"
        >
          <span className="flex items-center gap-3">
            <item.icon size={20} strokeWidth={1.5} />
            {item.label}
          </span>
          <ChevronDown size={16} className={`transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
        </button>
        {open && (
          <div className="pb-1">
            {item.children.map((c) => (
              <NavLink
                key={c.path}
                to={c.path}
                onClick={onNavigate}
                data-testid={`nav-link-${c.path}`}
                className={({ isActive }) =>
                  `block py-2 pl-[52px] pr-5 text-sm transition-colors duration-200 ${
                    isActive ? "border-r-2 border-[#D4AF37] bg-white/5 text-[#D4AF37]" : "text-white/60 hover:text-white"
                  }`
                }
              >
                {c.label}
              </NavLink>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <NavLink
      to={item.path}
      end={item.path === "/"}
      onClick={onNavigate}
      data-testid={`nav-link-${item.path}`}
      className={({ isActive }) =>
        `flex items-center gap-3 px-5 py-2.5 text-sm transition-colors duration-200 ${
          isActive
            ? "border-r-2 border-[#D4AF37] bg-white/5 text-[#D4AF37]"
            : "text-white/70 hover:bg-white/5 hover:text-white"
        }`
      }
    >
      <item.icon size={20} strokeWidth={1.5} />
      {item.label}
    </NavLink>
  );
};

const LEVEL_COLORS = { critical: "bg-red-500", warning: "bg-[#D4AF37]", info: "bg-blue-500", success: "bg-emerald-500" };

export default function Layout({ children }) {
  const { user, logout, hasPerm } = useAuth();
  const { siteName, logoUrl } = useBranding();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifs, setNotifs] = useState({ items: [], count: 0 });
  const notifRef = useRef(null);

  useEffect(() => {
    const load = () => api.get("/notifications").then((r) => setNotifs(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target)) setNotifOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const visibleNav = NAV.map((item) =>
    item.children
      ? { ...item, children: item.children.filter((c) => hasPerm(c.module)) }
      : item
  ).filter((item) => (item.children ? item.children.length > 0 : hasPerm(item.module)));

  const sidebar = (
    <div className="flex h-full w-64 flex-col bg-[#0B1F3A]">
      <div className="flex h-16 items-center gap-2 border-b border-white/10 px-5">
        {logoUrl ? (
          <img src={`${BACKEND_URL}${logoUrl}`} alt={siteName} className="h-8 w-8 object-contain" />
        ) : (
          <div className="flex h-8 w-8 items-center justify-center bg-[#D4AF37] font-heading text-sm font-bold text-[#0B1F3A]">
            {(siteName || "K").charAt(0).toUpperCase()}
          </div>
        )}
        <div>
          <p className="font-heading text-base font-semibold leading-tight text-white">{siteName || "KMS"}</p>
          <p className="text-[10px] uppercase tracking-[0.15em] text-white/50">Ticari Yönetim</p>
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto py-4" data-testid="sidebar-nav">
        {visibleNav.map((item) => (
          <NavItem key={item.label} item={item} onNavigate={() => setSidebarOpen(false)} />
        ))}
      </nav>
      <div className="border-t border-white/10 p-4">
        <button
          data-testid="logout-button"
          onClick={logout}
          className="flex w-full items-center gap-3 px-1 py-2 text-sm text-white/70 transition-colors duration-200 hover:text-[#D4AF37]"
        >
          <LogOut size={18} strokeWidth={1.5} /> Çıkış Yap
        </button>
        <p className="mt-2 px-1 text-[10px] text-white/30" data-testid="powered-by-footer">Powered By KMS Media Ajans</p>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen overflow-hidden">
      <aside className="hidden lg:block">{sidebar}</aside>
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 flex lg:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setSidebarOpen(false)} />
          <div className="relative z-10">{sidebar}</div>
        </div>
      )}
      <div className="flex flex-1 flex-col overflow-hidden">
        <header className="flex h-16 shrink-0 items-center justify-between border-b border-gray-200 bg-white px-4 md:px-8">
          <button className="lg:hidden" onClick={() => setSidebarOpen(true)} data-testid="mobile-menu-button">
            <Menu size={22} className="text-[#0B1F3A]" />
          </button>
          <div className="hidden lg:block" />
          <div className="flex items-center gap-4">
            <div className="relative" ref={notifRef}>
              <button
                data-testid="notifications-button"
                onClick={() => setNotifOpen(!notifOpen)}
                className="relative flex h-10 w-10 items-center justify-center border border-gray-200 text-[#0B1F3A] transition-colors duration-200 hover:border-[#D4AF37]"
              >
                <Bell size={19} strokeWidth={1.5} />
                {notifs.count > 0 && (
                  <span className="absolute -right-1.5 -top-1.5 flex h-5 min-w-5 items-center justify-center bg-[#D4AF37] px-1 text-[10px] font-bold text-[#0B1F3A]">
                    {notifs.count > 99 ? "99+" : notifs.count}
                  </span>
                )}
              </button>
              {notifOpen && (
                <div className="absolute right-0 top-12 z-50 w-80 border border-gray-200 bg-white" data-testid="notifications-panel">
                  <div className="border-b border-gray-200 px-4 py-3">
                    <p className="font-heading text-sm font-semibold">Bildirimler</p>
                  </div>
                  <div className="max-h-96 overflow-y-auto">
                    {notifs.items.length === 0 && (
                      <p className="px-4 py-6 text-center text-sm text-gray-500">Bildirim yok</p>
                    )}
                    {notifs.items.map((n, i) => (
                      <div key={i} className="flex gap-3 border-b border-gray-100 px-4 py-3">
                        <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${LEVEL_COLORS[n.level] || "bg-gray-400"}`} />
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{n.title}</p>
                          <p className="text-sm text-[#0B1F3A]">{n.message}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="flex items-center gap-3 border-l border-gray-200 pl-4">
              <div className="flex h-9 w-9 items-center justify-center bg-[#0B1F3A] text-sm font-semibold text-[#D4AF37]">
                {user?.name?.charAt(0)?.toUpperCase()}
              </div>
              <div className="hidden sm:block">
                <p className="text-sm font-medium leading-tight" data-testid="header-user-name">{user?.name}</p>
                <p className="text-xs text-gray-500">{user?.role === "super_admin" ? "Super Admin" : "Personel"}</p>
              </div>
            </div>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto p-4 md:p-8">{children}</main>
      </div>
    </div>
  );
}
